import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Tooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from '../components/ui/tooltip';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import supportService from '../services/supportService';
import { SupportThread, SupportMessage, ChannelType, SupportContact } from '../types/support-ticket';
import { formatDistanceToNow, format } from 'date-fns';
import { 
  Reply, 
  ReplyAll, 
  Forward, 
  Clock, 
  Trash2, 
  MoreHorizontal, 
  Paperclip, 
  ChevronDown, 
  ChevronRight, 
  Inbox, 
  Mail, 
  MessageSquare, 
  Smartphone, 
  Tag, 
  User, 
  FileText, 
  Send,
  Bold,
  Italic,
  List,
  ListOrdered,
  Link,
  Image
} from 'lucide-react';

// Editor rico para formatação de emails
const RichTextEditor = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => {
  const handleFormat = (format: string) => {
    // Em uma implementação real, seria usado um editor rico como TinyMCE, CKEditor ou Quill
    // Isso é apenas um exemplo simplificado
    switch (format) {
      case 'bold':
        onChange(`${value} **texto em negrito**`);
        break;
      case 'italic':
        onChange(`${value} *texto em itálico*`);
        break;
      case 'list':
        onChange(`${value}\n- Item 1\n- Item 2`);
        break;
      case 'ordered-list':
        onChange(`${value}\n1. Item 1\n2. Item 2`);
        break;
      case 'link':
        onChange(`${value} [texto do link](https://exemplo.com)`);
        break;
      default:
        break;
    }
  };

  return (
    <div className="border rounded-md">
      <div className="flex items-center gap-1 border-b p-2 bg-gray-50">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => handleFormat('bold')}
          title="Negrito"
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => handleFormat('italic')} 
          title="Itálico"
        >
          <Italic className="h-4 w-4" />
        </Button>
        <div className="h-4 w-px bg-gray-300 mx-1" />
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => handleFormat('list')} 
          title="Lista não ordenada"
        >
          <List className="h-4 w-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => handleFormat('ordered-list')} 
          title="Lista ordenada"
        >
          <ListOrdered className="h-4 w-4" />
        </Button>
        <div className="h-4 w-px bg-gray-300 mx-1" />
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => handleFormat('link')} 
          title="Inserir link"
        >
          <Link className="h-4 w-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="icon"
          title="Anexar imagem"
        >
          <Image className="h-4 w-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="icon"
          title="Anexar arquivo"
        >
          <Paperclip className="h-4 w-4" />
        </Button>
      </div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full p-3 min-h-[150px] focus:outline-none resize-y"
        placeholder="Digite sua mensagem..."
      />
      <div className="p-2 border-t bg-gray-50 flex items-center justify-between">
        <div className="flex items-center text-xs text-gray-500">
          <input type="checkbox" id="add-signature" className="mr-1.5" />
          <label htmlFor="add-signature">Incluir assinatura</label>
        </div>
        <div>
          <Button variant="outline" className="mr-2">Salvar rascunho</Button>
          <Button type="submit">
            <Send className="h-4 w-4 mr-2" />
            Enviar
          </Button>
        </div>
      </div>
    </div>
  );
};

// Componente para exibir informações detalhadas de email
const EmailDetailView = ({ 
  message, 
  contact
}: { 
  message: SupportMessage; 
  contact?: SupportContact;
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border rounded-lg mb-4">
      <div className="p-3 bg-gray-50 border-b flex items-center justify-between">
        <div className="flex items-center">
          <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 mr-3">
            {message.sender_type === 'contact' ? contact?.name?.charAt(0) || 'C' : 'A'}
          </div>
          <div>
            <div className="font-extrabold">
              {message.sender_type === 'contact' 
                ? contact?.name || 'Cliente' 
                : 'Agente de Suporte'}
            </div>
            <div className="text-xs text-gray-500 flex items-center">
              <span>{format(new Date(message.created_at), 'dd/MM/yyyy HH:mm')}</span>
              <button 
                onClick={() => setExpanded(!expanded)}
                className="ml-2 text-blue-600 hover:underline flex items-center"
              >
                {expanded ? 'Ocultar detalhes' : 'Mostrar detalhes'}
                {expanded ? <ChevronDown className="h-3 w-3 ml-1" /> : <ChevronRight className="h-3 w-3 ml-1" />}
              </button>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" title="Responder">
            <Reply className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" title="Responder a todos">
            <ReplyAll className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" title="Encaminhar">
            <Forward className="h-4 w-4" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem>Marcar como não lido</DropdownMenuItem>
              <DropdownMenuItem>Adicionar etiqueta</DropdownMenuItem>
              <DropdownMenuItem>Imprimir</DropdownMenuItem>
              <DropdownMenuItem className="text-red-600">Excluir</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {expanded && (
        <div className="p-3 border-b bg-gray-50">
          <div className="grid grid-cols-[auto,1fr] gap-x-2 text-sm">
            <div className="text-gray-500">De:</div>
            <div>
              {message.sender_type === 'contact' 
                ? `${contact?.name || 'Cliente'} <${contact?.email || 'email@cliente.com'}>` 
                : 'Suporte <suporte@orgmanage.com>'}
            </div>
            
            <div className="text-gray-500">Para:</div>
            <div>
              {message.sender_type === 'contact' 
                ? 'Suporte <suporte@orgmanage.com>' 
                : `${contact?.name || 'Cliente'} <${contact?.email || 'email@cliente.com'}>`}
            </div>
            
            {message.sender_type === 'agent' && (
              <>
                <div className="text-gray-500">Cc:</div>
                <div>equipe@orgmanage.com</div>
              </>
            )}
            
            <div className="text-gray-500">Data:</div>
            <div>{format(new Date(message.created_at), 'dd/MM/yyyy HH:mm:ss')}</div>
            
            <div className="text-gray-500">Assunto:</div>
            <div className="font-extrabold">RE: Solicitação de suporte</div>
          </div>
        </div>
      )}

      <div className="p-4">
        <div className="whitespace-pre-wrap">{message.content}</div>
        
        {message.attachments && message.attachments.length > 0 && (
          <div className="mt-4 border-t pt-3">
            <div className="text-sm font-extrabold mb-2">Anexos ({message.attachments.length})</div>
            <div className="flex flex-wrap gap-2">
              {message.attachments.map((attachment) => (
                <div 
                  key={attachment.id} 
                  className="flex items-center border rounded-md p-2 bg-gray-50 text-sm"
                >
                  <FileText className="h-4 w-4 mr-2 text-blue-600" />
                  <span className="mr-2 truncate max-w-[150px]">{attachment.filename}</span>
                  <span className="text-xs text-gray-500">({Math.round(Math.random() * 1000)}KB)</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SupportInbox = () => {
  const [threads, setThreads] = useState<SupportThread[]>([]);
  const [filteredThreads, setFilteredThreads] = useState<SupportThread[]>([]);
  const [selectedThread, setSelectedThread] = useState<SupportThread | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [contacts, setContacts] = useState<SupportContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<ChannelType | 'all'>('all');
  const [replyMessage, setReplyMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isFullEmailView, setIsFullEmailView] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load threads
  useEffect(() => {
    const loadThreads = async () => {
      setLoading(true);
      try {
        const allThreads = await supportService.getThreads({ isActive: true });
        setThreads(allThreads);
        setFilteredThreads(allThreads);

        const allContacts = await supportService.getContacts();
        setContacts(allContacts);
      } catch (error) {
        console.error('Failed to load threads', error);
      } finally {
        setLoading(false);
      }
    };

    loadThreads();
  }, []);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Filter threads based on active tab and search term
  useEffect(() => {
    let filtered = [...threads];

    // Filter by channel
    if (activeTab !== 'all') {
      filtered = filtered.filter(thread => thread.channel === activeTab);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(thread => 
        thread.subject?.toLowerCase().includes(term)
      );
    }

    setFilteredThreads(filtered);
  }, [threads, activeTab, searchTerm]);

  // Load messages when a thread is selected
  useEffect(() => {
    if (!selectedThread) {
      setMessages([]);
      return;
    }

    const loadMessages = async () => {
      try {
        const threadMessages = await supportService.getMessagesByThreadId(selectedThread.id);
        setMessages(threadMessages);
      } catch (error) {
        console.error('Failed to load messages', error);
      }
    };

    loadMessages();
  }, [selectedThread]);

  const handleSelectThread = async (thread: SupportThread) => {
    setSelectedThread(thread);
    
    // Se for um email, ativar a visualização detalhada por padrão
    if (thread.channel === 'email') {
      setIsFullEmailView(true);
    } else {
      setIsFullEmailView(false);
    }
  };

  const handleSendReply = async () => {
    if (!selectedThread || !replyMessage.trim()) return;

    try {
      const newMessage = await supportService.createMessage({
        thread_id: selectedThread.id,
        sender_type: 'agent',
        sender_id: 'agent-1', // In a real app, this would be the logged-in agent's ID
        content: replyMessage
      });

      setMessages([...messages, newMessage]);
      setReplyMessage('');
    } catch (error) {
      console.error('Failed to send message', error);
    }
  };

  const handleCreateTicket = async () => {
    if (!selectedThread) return;

    try {
      await supportService.createTicketFromThread(selectedThread.id, {
        priority: 'medium',
        status: 'new'
      });
      alert('Ticket created successfully!');
    } catch (error) {
      console.error('Failed to create ticket', error);
    }
  };

  const handleCloseThread = async () => {
    if (!selectedThread) return;

    try {
      await supportService.closeThread(selectedThread.id);
      
      // Refresh threads list
      const updatedThreads = await supportService.getThreads({ isActive: true });
      setThreads(updatedThreads);
      
      // Clear selected thread
      setSelectedThread(null);
    } catch (error) {
      console.error('Failed to close thread', error);
    }
  };

  const getChannelIcon = (channel: ChannelType) => {
    switch (channel) {
      case 'email':
        return <Mail className="h-5 w-5" />;
      case 'whatsapp':
        return <Smartphone className="h-5 w-5" />;
      case 'live_chat':
        return <MessageSquare className="h-5 w-5" />;
      default:
        return <Inbox className="h-5 w-5" />;
    }
  };

  const getChannelColor = (channel: ChannelType) => {
    switch (channel) {
      case 'email':
        return 'bg-blue-100 text-blue-700';
      case 'whatsapp':
        return 'bg-green-100 text-green-700';
      case 'live_chat':
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getContactById = (id: string) => {
    return contacts.find(contact => contact.id === id);
  };

  return (
    <div className="container mx-auto py-6">
      <Helmet>
        <title>CSM | Trivium - Caixa de Entrada</title>
      </Helmet>
      
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-extrabold">Caixa de Entrada</h1>
        <div className="space-x-2">
          <Select>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filtrar por status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="unassigned">Não atribuídos</SelectItem>
              <SelectItem value="assigned">Atribuídos</SelectItem>
            </SelectContent>
          </Select>
          <Select>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Ordenar por" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Mais recentes</SelectItem>
              <SelectItem value="oldest">Mais antigos</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="grid grid-cols-12 gap-6 h-[calc(100vh-200px)]">
        {/* Barra lateral */}
        <div className="col-span-4 border rounded-lg overflow-hidden shadow-sm">
          <div className="p-4 border-b">
            <Input 
              placeholder="Pesquisar conversas..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>
          
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as ChannelType | 'all')}>
            <div className="px-4 pt-2">
              <TabsList className="grid grid-cols-4 w-full">
                <TabsTrigger value="all">Todos</TabsTrigger>
                <TabsTrigger value="email">Email</TabsTrigger>
                <TabsTrigger value="live_chat">Chat</TabsTrigger>
                <TabsTrigger value="whatsapp">WhatsApp</TabsTrigger>
              </TabsList>
            </div>
            
            <TabsContent value="all" className="m-0">
              <ThreadList 
                threads={filteredThreads} 
                selectedThreadId={selectedThread?.id}
                onSelectThread={handleSelectThread}
                getChannelIcon={getChannelIcon}
                getChannelColor={getChannelColor}
                contacts={contacts}
              />
            </TabsContent>
            
            <TabsContent value="email" className="m-0">
              <ThreadList 
                threads={filteredThreads} 
                selectedThreadId={selectedThread?.id}
                onSelectThread={handleSelectThread}
                getChannelIcon={getChannelIcon}
                getChannelColor={getChannelColor}
                contacts={contacts}
              />
            </TabsContent>
            
            <TabsContent value="live_chat" className="m-0">
              <ThreadList 
                threads={filteredThreads} 
                selectedThreadId={selectedThread?.id}
                onSelectThread={handleSelectThread}
                getChannelIcon={getChannelIcon}
                getChannelColor={getChannelColor}
                contacts={contacts}
              />
            </TabsContent>
            
            <TabsContent value="whatsapp" className="m-0">
              <ThreadList 
                threads={filteredThreads} 
                selectedThreadId={selectedThread?.id}
                onSelectThread={handleSelectThread}
                getChannelIcon={getChannelIcon}
                getChannelColor={getChannelColor}
                contacts={contacts}
              />
            </TabsContent>
          </Tabs>
        </div>
        
        {/* Conteúdo principal */}
        <div className="col-span-8 border rounded-lg overflow-hidden shadow-sm">
          {selectedThread ? (
            <div className="flex flex-col h-full">
              {/* Cabeçalho da conversa */}
              <div className="p-4 border-b flex justify-between items-center bg-gray-50">
                <div>
                  <div className="flex items-center gap-2">
                    <div className={`p-1.5 rounded-md ${getChannelColor(selectedThread.channel)}`}>
                      {getChannelIcon(selectedThread.channel)}
                    </div>
                    <h2 className="text-lg font-extrabold">
                      {selectedThread.subject || 'Nova conversa'}
                    </h2>
                    <Badge variant="outline">
                      {selectedThread.channel === 'email' ? 'Email' : 
                       selectedThread.channel === 'whatsapp' ? 'WhatsApp' : 'Chat'}
                    </Badge>
                    {selectedThread.channel === 'email' && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center bg-yellow-100 text-yellow-800 rounded-md text-xs px-2 py-0.5">
                              <Clock className="h-3 w-3 mr-1" />
                              <span>SLA: 4h</span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Prazo para primeira resposta vence em 3h 45min</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">
                    Iniciado {formatDistanceToNow(new Date(selectedThread.created_at), { addSuffix: true })}
                    {selectedThread.channel === 'email' && 
                      ` • De: ${getContactById(selectedThread.contact_id)?.email || 'cliente@email.com'}`}
                  </p>
                </div>
                <div className="space-x-2">
                  {selectedThread.channel === 'email' && (
                    <>
                      <Button variant="outline" size="sm" className="gap-1">
                        <Tag className="h-4 w-4 mr-1" />
                        Adicionar etiqueta
                      </Button>
                      <Button variant="outline" size="sm" className="gap-1">
                        <User className="h-4 w-4 mr-1" />
                        Atribuir
                      </Button>
                    </>
                  )}
                  <Button onClick={handleCreateTicket} variant="outline">Criar Ticket</Button>
                  <Button onClick={handleCloseThread} variant="outline">
                    <Trash2 className="h-4 w-4 mr-1.5" />
                    Arquivar
                  </Button>
                </div>
              </div>
              
              {/* Mensagens */}
              <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
                {selectedThread.channel === 'email' && isFullEmailView ? (
                  <div>
                    {messages.map((message) => (
                      <EmailDetailView 
                        key={message.id} 
                        message={message} 
                        contact={getContactById(selectedThread.contact_id)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((message) => (
                      <div 
                        key={message.id} 
                        className={`flex ${message.sender_type === 'agent' ? 'justify-end' : 'justify-start'}`}
                      >
                        <Card className={`max-w-[80%] ${message.sender_type === 'agent' ? 'bg-primary text-primary-foreground' : 'bg-white'}`}>
                          <CardContent className="p-3">
                            <p>{message.content}</p>
                            <div className="text-xs mt-1 opacity-80">
                              {format(new Date(message.created_at), 'HH:mm')}
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>
              
              {/* Caixa de resposta */}
              <div className="p-4 border-t bg-white">
                {selectedThread.channel === 'email' ? (
                  <form onSubmit={(e) => { e.preventDefault(); handleSendReply(); }}>
                    <RichTextEditor value={replyMessage} onChange={setReplyMessage} />
                  </form>
                ) : (
                  <div className="flex space-x-2">
                    <Input 
                      placeholder="Digite sua resposta..." 
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      className="flex-1"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendReply();
                        }
                      }}
                    />
                    <Button onClick={handleSendReply}>Enviar</Button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <h3 className="text-xl font-extrabold mb-2">Selecione uma conversa</h3>
                <p className="text-gray-500">Escolha uma conversa à esquerda para começar</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Component para lista de threads
interface ThreadListProps {
  threads: SupportThread[];
  selectedThreadId?: string;
  onSelectThread: (thread: SupportThread) => void;
  getChannelIcon: (channel: ChannelType) => React.ReactNode;
  getChannelColor: (channel: ChannelType) => string;
  contacts: SupportContact[];
}

const ThreadList = ({ 
  threads, 
  selectedThreadId, 
  onSelectThread, 
  getChannelIcon, 
  getChannelColor, 
  contacts 
}: ThreadListProps) => {
  const getContactName = (contactId: string) => {
    const contact = contacts.find(c => c.id === contactId);
    return contact?.name || 'Cliente';
  };

  if (threads.length === 0) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Nenhuma conversa encontrada</p>
      </div>
    );
  }

  return (
    <div className="overflow-y-auto max-h-[calc(100vh-270px)]">
      {threads.map((thread) => (
        <div 
          key={thread.id}
          className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors
            ${selectedThreadId === thread.id ? 'bg-gray-100' : ''}`}
          onClick={() => onSelectThread(thread)}
        >
          <div className="flex items-start gap-3">
            <div className={`shrink-0 p-1.5 rounded-md ${getChannelColor(thread.channel)}`}>
              {getChannelIcon(thread.channel)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex justify-between items-start mb-1">
                <h3 className="font-extrabold truncate pr-2">{thread.subject || 'Nova conversa'}</h3>
                <span className="text-xs text-gray-500 whitespace-nowrap">
                  {format(new Date(thread.last_message_at), 'HH:mm')}
                </span>
              </div>
              <p className="text-sm font-extrabold truncate mb-1">
                {getContactName(thread.contact_id)}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {thread.channel === 'email' ? 'Email: ' : thread.channel === 'whatsapp' ? 'WhatsApp: ' : 'Chat: '}
                Última atividade {formatDistanceToNow(new Date(thread.last_message_at), { addSuffix: true })}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default SupportInbox; 